import { useState, useEffect, useRef, forwardRef, useImperativeHandle, } from "react"
import styled from 'styled-components'
import Fieldset from "components/_UI/Fieldset"
import Radio from "components/_UI/Radio"
import TextArea from "components/_UI/TextArea"
import Text from "components/_UI/Text"
import Modal from "components/_UI/Modal"
import Button from "components/_UI/Button"
import { Column } from "styled/alignment/Column"
import colors from "theme/colors"
import fonts from "theme/fonts"
import { StyledLabel } from "styled/UI/StyledForm"
import ImageUpload from "components/_UI/ImageUpload"
import allowedFilesTypes from "texts/messages/allowedFilesTypes"
import validateInputFile from "helpers/validate-input-file"
import imageUploadApi from 'api/requests/imageUpload';
import { Cake, Calendar, Clock } from 'lucide-react';
import masks from 'helpers/masks';
import regex from 'helpers/regex';
import fieldValidations from 'helpers/fieldValidations';

// Cada tipo mascarado aqui tem uma máscara fixa. CPF e CNPJ são tipos
// separados (não um campo híbrido) porque o react-input-mask trava a
// digitação assim que a máscara atual enche — não dá pra trocar de CPF pra
// CNPJ no meio da digitação sem perder caracteres.
const FIELD_MASKS = {
    phone: masks.phone,
    cep: masks.cep,
    cpf: masks.cpf,
    cnpj: masks.cnpj,
};

// CPF usa o checksum já existente em fieldValidations — mesma validação do
// resto do app. CNPJ segue o mesmo padrão já usado em fieldValidations.cnpj()
// (só formato, sem checksum) pra não divergir do que já existe.
const FORMAT_VALIDATORS = {
    phone: (value) => regex.phone.test(value) ? '' : 'Telefone inválido',
    cep: (value) => regex.cep.test(value) ? '' : 'CEP inválido',
    email: (value) => regex.email.test(value) ? '' : 'E-mail inválido',
    cnpj: (value) => regex.cnpj.test(value) ? '' : 'CNPJ inválido',
    cpf: (value) => {
        const result = fieldValidations.cpf().validate.validCpf(value);
        return result === true ? '' : result;
    },
};

function getFieldFormatError(field, value) {
    if (!value) return '';

    const validate = FORMAT_VALIDATORS[field.type];
    return validate ? validate(value) : '';
}

const CustomFieldsForm = forwardRef(({ customFields }, ref) => {
    const rootRef = useRef()
    const [customFieldsValues, setCustomFieldsValues] = useState({})
    const [customFieldsErrors, setCustomFieldsErrors] = useState({})
    const [modalTitle, setModalTitle] = useState()
    const [modalData, setModalData] = useState()

    const labelMaxLength = 200

    useImperativeHandle(ref, () => ({
        validate() {
            return validateAllFields()
        },
        getCustomFieldsValues() {
            return customFieldsValues
        },
        scrollIntoView: (options) => {
            rootRef.current.scrollIntoView(options)
        },
    }));

    useEffect(() => {
        setCustomFieldsValues((prev) => {
            return {
                ...prev,
                ...createCustomFieldsVariables(customFields)
            }
        })

        setCustomFieldsErrors((prev) => {
            return {
                ...prev,
                ...createCustomFieldsErrors(customFields)
            }
        })
    }, [])

    function createCustomFieldsVariables(customFields) {
        const result = {}

        customFields.forEach(field => {
            if (field.type === 'radio') {
                result[field.id] = []

                return
            }

            if (field.type === 'checkbox') {
                result[field.id] = []

                return
            }

            if (field.type === 'upload') {
                result[field.id] = {}

                return
            }

            result[field.id] = ''
        })

        return result
    }

    function createCustomFieldsErrors(customFields) {
        const result = {}

        customFields.forEach(field => {
            result[field.id] = ''
        })

        return result
    }

    function handleOnChange(field, value) {
        const requiredError = field.required && !value ? 'Campo obrigatório' : ''
        const formatError = requiredError ? '' : getFieldFormatError(field, value)

        setCustomFieldsErrors((prev) => {
            return {
                ...prev,
                [field.id]: requiredError || formatError
            }
        })

        setCustomFieldsValues((prev) => {
            return {
                ...prev,
                [field.id]: value
            }
        })
    }

    function handleRadioChange(field, value) {
        setCustomFieldsValues((prev) => {
            return {
                ...prev,
                [field.id]: [value]
            }
        })
    }

    function handleCheckboxChange(field, value) {
        if (customFieldsValues[field.id].includes(value)) {
            const newCheckboxValues = customFieldsValues[field.id].filter(fieldValue => fieldValue !== value)

            setCustomFieldsValues((prev) => {
                return {
                    ...prev,
                    [field.id]: newCheckboxValues
                }
            })

            return
        }

        setCustomFieldsValues((prev) => {
            return {
                ...prev,
                [field.id]: [...prev[field.id], value]
            }
        })
    }

    async function handleFileChange(field, [file]) {
        const additionalFileTypes = ['application/pdf']
        const validationFile = validateInputFile(file, 5, additionalFileTypes)

        if (!validationFile.isValid) {
            setCustomFieldsErrors((prev) => {
                return {
                    ...prev,
                    [field.id]: validationFile.message
                }
            })
            return
        }

        const formData = new FormData()
        formData.append('image', file)
        formData.append('folder', 'event_custom_fields')

        try {
            const { data } = await imageUploadApi.uploadWithFolder(formData)

            setCustomFieldsValues((prev) => {
                return {
                    ...prev,
                    [field.id]: {
                        id: data.id,
                        url: data.path
                    }
                }
            })
        } catch (error) {
            setCustomFieldsErrors((prev) => {
                return {
                    ...prev,
                    [field.id]: error?.response?.data?.message || error?.message
                }
            })
        }
    }

    async function handleFileDelete(field) {
        const fileId = customFieldsValues[field.id].id

        setCustomFieldsErrors((prev) => {
            return {
                ...prev,
                [field.id]: ''
            }
        })

        if (!fileId) {
            return
        }

        try {
            await imageUploadApi.delete(fileId)

            setCustomFieldsValues((prev) => {
                return {
                    ...prev,
                    [field.id]: {}
                }
            })
        } catch (error) {
            setCustomFieldsErrors((prev) => {
                return {
                    ...prev,
                    [field.id]: error?.response?.data?.message || error?.message
                }
            })
        }
    }

    function validateAllFields() {
        let hasError = false

        Object.values(customFields).forEach((field) => {
            if (field.required) {
                if (field.type === 'radio') {
                    setCustomFieldsErrors((prev) => {
                        return {
                            ...prev,
                            [field.id]: customFieldsValues[field.id].length ? '' : 'Campo obrigatório'
                        }
                    })

                    if (customFieldsValues[field.id].length === 0) hasError = true

                    return
                }

                if (field.type === 'checkbox') {
                    setCustomFieldsErrors((prev) => {
                        return {
                            ...prev,
                            [field.id]: customFieldsValues[field.id].length ? '' : 'Campo obrigatório'
                        }
                    })

                    if (customFieldsValues[field.id].length === 0) hasError = true

                    return
                }

                if (field.type === 'upload') {
                    setCustomFieldsErrors((prev) => {
                        return {
                            ...prev,
                            [field.id]: customFieldsValues[field.id]?.url ? '' : 'Campo obrigatório'
                        }
                    })

                    if (!customFieldsValues[field.id]?.url) hasError = true

                    return
                }

                setCustomFieldsErrors((prev) => {
                    return {
                        ...prev,
                        [field.id]: customFieldsValues[field.id] ? '' : 'Campo obrigatório'
                    }
                })

                if (!customFieldsValues[field.id]) hasError = true
            }

            // Formato é checado independente de obrigatório: um CPF opcional
            // preenchido errado também não pode passar pro pagamento.
            const formatError = getFieldFormatError(field, customFieldsValues[field.id])

            if (formatError) {
                setCustomFieldsErrors((prev) => {
                    return {
                        ...prev,
                        [field.id]: formatError
                    }
                })

                hasError = true
            }
        })

        return hasError
    }

    function showDataModal (title, data) {
        setModalTitle(title)
        setModalData(data)
    }

    return (
        <CustomFieldsContainer
            ref={rootRef}
        >
            {
                customFields.map(field => {
                    if (field.type === 'radio') {
                        return (
                            <div
                                key={field.id}
                            >
                                <StyledLabel>{field.label}</StyledLabel>
                                <ListGrid marginTop='6px'>
                                    {
                                        field.options.map(option => {
                                            let truncatedLabel
                                            if (option?.length > labelMaxLength) {
                                                truncatedLabel = option.substring(0, labelMaxLength) + '...'
                                            }

                                            return (
                                                <div key={option}>
                                                    <Radio
                                                        label={truncatedLabel || option}
                                                        selected={customFieldsValues[field.id]?.some(value => value === option)}
                                                        onSelect={() => handleRadioChange(field, option)}
                                                        marginTop={'0'}
                                                        textStyle={{
                                                            overflowWrap: 'anywhere'
                                                        }}
                                                    />
                                                    {
                                                        truncatedLabel &&
                                                            <Button
                                                                category="outline"
                                                                marginLeft='24px'
                                                                style={{width: 'auto'}}
                                                                onClick={() => showDataModal(field.label, option)}
                                                            >
                                                                Ver mais
                                                            </Button>
                                                    }
                                                </div>
                                            )
                                        })
                                    }
                                </ListGrid>
                                <Text
                                    color={colors.danger}
                                    name='small'
                                >
                                    {customFieldsErrors[field.id]}
                                </Text>
                            </div>
                        )
                    }

                    if (field.type === 'checkbox') {
                        return (
                            <div
                                key={field.id}
                            >
                                <StyledLabel>{field.label}</StyledLabel>
                                <ListGrid marginTop='6px'>
                                    {
                                        field.options.map(option => {
                                            return (
                                                <SelectItem
                                                    key={option}
                                                    selected={customFieldsValues[field.id]?.includes(option)}
                                                    onClick={() => handleCheckboxChange(field, option)}
                                                >
                                                    <Text
                                                        name='mini'
                                                        textAlign={'center'}
                                                        paddingBottom={0}
                                                        type='bold'
                                                        ellipsisAt={'98%'}
                                                        color={customFieldsValues[field.id]?.includes(option) ? colors.neutral : colors.night}
                                                    >
                                                        {option}
                                                    </Text>
                                                </SelectItem>
                                            )
                                        })
                                    }
                                </ListGrid>
                                <Text
                                    color={colors.danger}
                                    name='small'
                                >
                                    {customFieldsErrors[field.id]}
                                </Text>
                            </div>
                        )
                    }

                    if (field.type === 'textarea') {
                        return (
                            <div
                                key={field.id}
                            >
                                <StyledLabel>{field.label}</StyledLabel>
                                <TextArea
                                    marginTop='6px'
                                    name={field.label}
                                    // register={() => { }}
                                    onChangeValue={(value) => handleOnChange(field, value)}
                                    squired
                                />
                                <Text
                                    color={colors.danger}
                                    name='small'
                                >
                                    {customFieldsErrors[field.id]}
                                </Text>
                            </div>
                        )
                    }

                    if (field.type === 'upload') {
                        return (
                            <div>
                                <Text size='small' type='bold' color={colors.nightDark}>
                                    {field.label}
                                </Text>
                                <FileContainer>
                                    <ImageUpload
                                        onToggleFile={(file) => handleFileChange(field, file)}
                                        onDeleteFile={() => handleFileDelete(field)}
                                        product={false}
                                        isEdit={false}
                                        refreshTryAgain={() => {
                                            setCustomFieldsErrors((prev) => {
                                                return {
                                                    ...prev,
                                                    [field.id]: ''
                                                }
                                            })
                                        }}
                                    />
                                </FileContainer>

                                <Column verticalCenter>
                                    <Text name='small' marginTop={'10px'}>Arquivos permitidos: {allowedFilesTypes.imagesAndPdf}.</Text>
                                    <Text name='small'>Tamanho máximo permitido: 2 MB</Text>
                                    <Text
                                        color={colors.danger}
                                        name='small'
                                    >
                                        {customFieldsErrors[field.id]}
                                    </Text>
                                </Column>
                            </div>
                        )
                    }

                    if (field.type === 'date' || field.type === 'time' || field.type === 'birth_date') {
                        // "Data de nascimento" é só uma etiqueta mais amigável pro
                        // mesmo input nativo de data — o atributo `type` do HTML só
                        // aceita 'date'/'time', nunca o valor de field.type direto.
                        const nativeType = field.type === 'time' ? 'time' : 'date'

                        return (
                            <div key={field.id}>
                                <StyledLabel>{field.label}</StyledLabel>
                                <DateTimeWrapper onClick={(e) => {
                                    const input = e.currentTarget.querySelector('input')
                                    if (input?.showPicker) {
                                        input.showPicker()
                                    } else {
                                        input?.focus()
                                    }
                                }}>
                                    <DateTimeIcon>
                                        {field.type === 'time'
                                            ? <Clock size={18} color={colors.night} />
                                            : field.type === 'birth_date'
                                                ? <Cake size={18} color={colors.night} />
                                                : <Calendar size={18} color={colors.night} />
                                        }
                                    </DateTimeIcon>
                                    <NativeDateTimeInput
                                        type={nativeType}
                                        onChange={(e) => handleOnChange(field, e.target.value)}
                                    />
                                </DateTimeWrapper>
                                <Text
                                    color={colors.danger}
                                    name='small'
                                >
                                    {customFieldsErrors[field.id]}
                                </Text>
                            </div>
                        )
                    }

                    {
                        return (
                            <div
                                key={field.id}
                            >
                                <Fieldset
                                    layout='squired'
                                    inputType={field.type === 'email' ? 'email' : 'text'}
                                    mask={FIELD_MASKS[field.type]}
                                    label={field.label}
                                    onChangeValue={(value) => handleOnChange(field, value)}
                                />
                                <Text
                                    color={colors.danger}
                                    name='small'
                                >
                                    {customFieldsErrors[field.id]}
                                </Text>
                            </div>
                        )
                    }
                })
            }
            <Modal
                size='large'
                title={modalTitle}
                isOpen={Boolean(modalData)}
                closeButton={true}
                onClose={() => setModalData(false)}
            >
                <Text color={colors.night}>
                    { modalData }
                </Text>
            </Modal>
        </CustomFieldsContainer>
    )
})

const CustomFieldsContainer = styled.div`
    width: 100%;
`

const SelectItem = styled.div`
    border: 1.5px solid ${colors.nightLight};
    padding: 10px 14px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all ease .2s;

    &:hover {
        border-color: ${colors.occasionalPurple};
    }

    ${({ selected }) => selected && `
        background: ${colors.occasionalPurple};
        border-color: ${colors.occasionalPurple};
        & > p {
            color: ${colors.neutralLight} !important;
        }
    `};
`;

const FileContainer = styled.div`
    & > div > div {
        width: 110px;
        height: 110px;
    }
`;

const ListGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 10px;

    ${({ marginTop }) => marginTop && `margin-top: ${marginTop}`};
`;

const DateTimeWrapper = styled.div`
    display: flex;
    align-items: center;
    background: ${colors.neutralLight};
    border: 1.5px solid #D4D4D4;
    border-radius: 8px;
    margin-top: 6px;
    padding: 0 14px;
    cursor: pointer;
    transition: all ease .25s;

    &:hover {
        border-color: ${colors.nightLight};
    }

    &:focus-within {
        border-color: ${colors.occasionalPurple};
        box-shadow: 0 0 0 3px ${colors.occasionalPurple}1A;
    }
`;

const DateTimeIcon = styled.div`
    display: flex;
    align-items: center;
    margin-right: 10px;
    pointer-events: none;
`;

const NativeDateTimeInput = styled.input`
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    padding: 11px 0;
    font-size: ${fonts.sizes.default};
    color: ${colors.nightDark};
    cursor: pointer;

    &::-webkit-calendar-picker-indicator {
        display: none;
        -webkit-appearance: none;
    }
`;

export default CustomFieldsForm